import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  formatHours,
  listOvertimeOpeningBalances,
  upsertOvertimeOpeningBalance,
  type EmployeeOvertimeOpeningBalance,
} from "@/lib/employeeTimes";

interface EmployeeOvertimeOpeningBalanceEditorProps {
  userLogin: string;
  userDisplay: string;
  refreshKey?: number;
  onChanged?: () => void;
}

interface BalanceDraft {
  effective_date: string;
  hours_decimal: string;
  note: string;
}

function balanceToDraft(row: EmployeeOvertimeOpeningBalance): BalanceDraft {
  return {
    effective_date: row.effective_date.slice(0, 10),
    hours_decimal: String(row.hours_decimal),
    note: row.note ?? "",
  };
}

function formatDateDe(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function EmployeeOvertimeOpeningBalanceEditor({
  userLogin,
  userDisplay,
  refreshKey = 0,
  onChanged,
}: EmployeeOvertimeOpeningBalanceEditorProps) {
  const [row, setRow] = useState<EmployeeOvertimeOpeningBalance | null>(null);
  const [draft, setDraft] = useState<BalanceDraft>({
    effective_date: new Date().toISOString().slice(0, 10),
    hours_decimal: "0",
    note: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listOvertimeOpeningBalances({ user_login: userLogin });
      const found = data[0] ?? null;
      if (found) {
        setRow(found);
        setDraft(balanceToDraft(found));
      } else {
        const today = new Date().toISOString().slice(0, 10);
        setRow(null);
        setDraft({ effective_date: today, hours_decimal: "0", note: "" });
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Überstunden-Startstand konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [userLogin]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const handleSave = async () => {
    if (!draft.effective_date) {
      toast.error("Bitte einen Stichtag angeben.");
      return;
    }
    setSaving(true);
    try {
      const hours = Number(draft.hours_decimal.replace(",", "."));
      if (!Number.isFinite(hours)) {
        toast.error("Bitte einen gültigen Überstundenstand eingeben.");
        return;
      }
      await upsertOvertimeOpeningBalance({
        user_login: userLogin,
        user_display: userDisplay,
        effective_date: draft.effective_date,
        hours_decimal: hours,
        note: draft.note.trim() || undefined,
      });
      toast.success("Startstand gespeichert.");
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 rounded-ios border border-separator bg-card p-4 shadow-ios">
      <div>
        <h2 className="text-lg font-semibold text-label">Überstunden-Startstand</h2>
        <p className="mt-1 text-sm text-label-secondary">
          Überstundenstand an einem Stichtag hinterlegen. Ab diesem Tag rechnet die App vorwärts
          (App- und Hero-Tageszeiten minus Soll, abzüglich Auszahlungen). Der Hero-Saldo wird nicht
          mehr verwendet.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="space-y-1">
            <span className="eh-label">Stichtag</span>
            <input
              type="date"
              className="eh-input w-full"
              value={draft.effective_date}
              onChange={(e) => setDraft((prev) => ({ ...prev, effective_date: e.target.value }))}
            />
          </label>
          <label className="space-y-1">
            <span className="eh-label">Startstand (Stunden)</span>
            <input
              type="text"
              inputMode="decimal"
              className="eh-input w-full"
              value={draft.hours_decimal}
              onChange={(e) => setDraft((prev) => ({ ...prev, hours_decimal: e.target.value }))}
            />
          </label>
          <label className="space-y-1 md:col-span-2">
            <span className="eh-label">Notiz</span>
            <input
              type="text"
              className="eh-input w-full"
              placeholder="z. B. Stand aus Alt-System"
              value={draft.note}
              onChange={(e) => setDraft((prev) => ({ ...prev, note: e.target.value }))}
            />
          </label>
          {row?.current_balance_hours != null && (
            <div className="md:col-span-2 rounded-lg bg-fill-tertiary/50 px-3 py-2 text-sm">
              <span className="text-label-secondary">Aktueller Stand: </span>
              <span className="font-semibold tabular-nums text-label">
                {formatHours(row.current_balance_hours)}
              </span>
              {row.updated_at && (
                <span className="ml-2 text-xs text-label-tertiary">
                  (zuletzt {formatDateDe(row.updated_at.slice(0, 10))})
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button disabled={loading || saving} onClick={() => void handleSave()}>
          Speichern
        </Button>
      </div>
    </div>
  );
}
